import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { TermsGate } from './components/TermsGate';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UnitsPage from './pages/UnitsPage';
import PatientsPage from './pages/PatientsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import UsersPage from './pages/UsersPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ProfilePage from './pages/ProfilePage';
import AuditLogsPage from './pages/AuditLogsPage';
import SlotsPage from './pages/SlotsPage';
import ProcedimentosPage from './pages/ProcedimentosPage';
import LicensesPage from './pages/LicensesPage';
import LicenseActivationPage from './pages/LicenseActivationPage';
import './App.css';

// Wrapper que redireciona Relator para /reports em rotas nao permitidas
const RelatorGuard = ({ children }) => {
  const { isRelator } = useAuth();
  if (isRelator()) return <Navigate to="/reports" replace />;
  return children;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <RelatorGuard>
                  <Layout>
                    <DashboardPage />
                  </Layout>
                </RelatorGuard>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/units"
            element={
              <ProtectedRoute minRole="admin_geral">
                <Layout>
                  <UnitsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/patients"
            element={
              <ProtectedRoute>
                <RelatorGuard>
                  <Layout>
                    <PatientsPage />
                  </Layout>
                </RelatorGuard>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <RelatorGuard>
                  <Layout>
                    <AppointmentsPage />
                  </Layout>
                </RelatorGuard>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/users"
            element={
              <ProtectedRoute requireAdmin={true}>
                <Layout>
                  <UsersPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/reports"
            element={
              <ProtectedRoute allowMarcadorGeral={true}>
                <Layout>
                  <ReportsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/slots"
            element={
              <ProtectedRoute>
                <RelatorGuard>
                  <Layout>
                    <SlotsPage />
                  </Layout>
                </RelatorGuard>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/procedimentos"
            element={
              <ProtectedRoute minRole="admin_geral">
                <Layout>
                  <ProcedimentosPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute minRole="admin_geral">
                <Layout>
                  <AuditLogsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute minRole="admin_geral">
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Layout>
                  <ProfilePage />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/licenses"
            element={
              <ProtectedRoute minRole="master">
                <Layout>
                  <LicensesPage />
                </Layout>
              </ProtectedRoute>
            }
          />

          <Route
            path="/activate"
            element={<LicenseActivationPage onActivated={() => window.location.href = '/login'} />}
          />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <TermsGate />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
